import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import useAuthStore from './store/auth';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyEmailPending from './pages/VerifyEmailPending';
import DashboardPage from './pages/DashboardPage';
import IncomePage from './pages/IncomePage';
import ExpensesPage from './pages/ExpensesPage';
import BudgetsPage from './pages/BudgetsPage';
import CategoriesPage from './pages/CategoriesPage';
import ReportsPage from './pages/ReportsPage';
import FamilyPage from './pages/FamilyPage';
import ProfilePage from './pages/ProfilePage';
import PricingPage from './pages/PricingPage';
import AdminPage from './pages/AdminPage';
import ReceiptPage from './pages/ReceiptPage';
import JoinFamilyPage from './pages/JoinFamilyPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10000,           // donnees fraiches 10s
      refetchOnWindowFocus: true, // refresh quand on revient sur l onglet
      refetchInterval: 30000,     // refresh auto toutes les 30s
      refetchIntervalInBackground: false,
    }
  }
});

function PrivateRoute({ children }) {
  const { token, user, loading } = useAuthStore();
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a0a0f', color:'#e8e8f0', fontFamily:'Syne,sans-serif', fontSize:18 }}>
      Chargement...
    </div>
  );
  if (!token) return <Navigate to="/login" replace />;
  // Bloquer si email non verifie
  if (user && user.email_verified === false) return <Navigate to="/verify-email-pending" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuthStore();
  return user?.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { fetchMe, token } = useAuthStore();
  useEffect(() => {
    if (token) fetchMe();
    else useAuthStore.setState({ loading: false });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster position="bottom-right" toastOptions={{ style:{ background:'#13131a', color:'#e8e8f0', border:'1px solid #2a2a3d' } }} />
        <Routes>
          <Route path="/login"                  element={<LoginPage />} />
          <Route path="/register"               element={<RegisterPage />} />
          <Route path="/forgot-password"        element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token"  element={<ResetPasswordPage />} />
          <Route path="/verify-email/:token"    element={<VerifyEmailPage />} />
          <Route path="/verify-email-pending"   element={<VerifyEmailPending />} />
          <Route path="/pricing"                element={<PricingPage />} />
          <Route path="/receipt"                element={<ReceiptPage />} />
          <Route path="/family/join/:token"     element={<JoinFamilyPage />} />
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"  element={<DashboardPage />} />
            <Route path="income"     element={<IncomePage />} />
            <Route path="expenses"   element={<ExpensesPage />} />
            <Route path="budgets"    element={<BudgetsPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="reports"    element={<ReportsPage />} />
            <Route path="family"     element={<FamilyPage />} />
            <Route path="profile"    element={<ProfilePage />} />
            <Route path="admin"      element={<AdminRoute><AdminPage /></AdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}